import { createProject, type DatabaseType, type Project } from '@hyphen-db-studio/schema-core';
import { parseDbml } from '@hyphen-db-studio/dbml-parser';
import { create } from 'zustand';
import { pushDiagram } from '../lib/syncDiagram';
import { SAMPLE_DBML } from '../lib/sampleSchema';

/** Client-side cache of the ER diagrams the signed-in user can see. The server is the source of truth: the
 * workspace store fills this from /api/bootstrap, and `upsert` writes edits back. */
interface ProjectsState {
  projects: Project[];
  hydrated: boolean;
  hydrate: () => void;
  setAll: (projects: Project[]) => void;
  createNew: (name: string, databaseType?: DatabaseType, seedSample?: boolean) => Project;
  remove: (id: string) => void;
  upsert: (project: Project) => void;
  get: (id: string) => Project | undefined;
}

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  projects: [],
  hydrated: false,

  hydrate: () => {},

  setAll: (projects) => set({ projects, hydrated: true }),

  /** Creates the diagram locally only; the workspace store persists it to the server. */
  createNew: (name, databaseType = 'postgresql', seedSample = false) => {
    const project = createProject(name, databaseType);
    if (seedSample) {
      const { schema } = parseDbml(SAMPLE_DBML);
      project.dbmlSource = SAMPLE_DBML;
      project.schema = schema;
    }
    set({ projects: [...get().projects, project] });
    return project;
  },

  remove: (id) => set({ projects: get().projects.filter((p) => p.id !== id) }),

  upsert: (project) => {
    set({
      projects: get().projects.some((p) => p.id === project.id)
        ? get().projects.map((p) => (p.id === project.id ? project : p))
        : [...get().projects, project],
    });
    pushDiagram(project.id, project.name, project);
  },

  get: (id) => get().projects.find((p) => p.id === id),
}));
